import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, UserCog, Search } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Admin: move an order (invoice) to another user. target = { invoiceId, invoiceName, amount, currentUserName }
export const ReassignUserDialog = ({ target, onClose, onDone }) => {
    const { getAuthHeaders } = useAuth();
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState('');
    const [sel, setSel] = useState(null);
    const [busy, setBusy] = useState(false);
    const open = !!target;

    useEffect(() => {
        if (!open) return;
        setSel(null); setQ('');
        axios.get(`${API}/api/users`, { headers: getAuthHeaders() })
            .then((r) => setUsers((r.data || []).filter((u) => u.role !== 'admin')))
            .catch(() => toast.error('No se pudieron cargar los usuarios'));
    }, [open, getAuthHeaders]);

    const list = users.filter((u) => u.name !== target?.currentUserName && `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));

    const confirm = async () => {
        if (!sel) return;
        setBusy(true);
        try {
            const r = await axios.patch(`${API}/api/invoices/${target.invoiceId}/user`, { user_id: sel.id }, { headers: getAuthHeaders() });
            toast.success(r.data.message + (r.data.moved_accounting ? (r.data.is_extra ? ' · ahora cuenta en Contabilidad Extra' : ' · ahora cuenta en contabilidad regular') : ''));
            onDone?.(r.data);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo reasignar la factura');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
            <DialogContent className="max-w-md" data-testid="reassign-user-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><UserCog className="w-5 h-5 text-fuchsia-600" /> Cambiar usuario de la orden</DialogTitle>
                    <DialogDescription>
                        <b>{target?.invoiceName}</b>{target?.amount != null && <> · ${Number(target.amount).toFixed(2)}</>} · actualmente de <b>{target?.currentUserName || '—'}</b>.
                        La orden pasará a contabilizarse en el usuario elegido (Dashboard, Totales, Extracciones y Depósitos).
                    </DialogDescription>
                </DialogHeader>
                <div className="relative">
                    <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar usuario..." className="pl-8" data-testid="reassign-search" />
                </div>
                <div className="max-h-64 overflow-y-auto divide-y rounded-lg border" data-testid="reassign-user-list">
                    {list.length === 0 && <p className="p-3 text-sm text-slate-400">Sin usuarios</p>}
                    {list.map((u) => (
                        <button
                            key={u.id}
                            type="button"
                            onClick={() => setSel(u)}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-slate-50 ${sel?.id === u.id ? 'bg-fuchsia-50 ring-1 ring-inset ring-fuchsia-300' : ''}`}
                            data-testid={`reassign-user-${u.id}`}
                        >
                            <span className="min-w-0">
                                <span className="font-medium text-slate-800 block truncate">{u.name}</span>
                                <span className="text-xs text-slate-400 block truncate">{u.email}</span>
                            </span>
                            <span className="flex gap-1 shrink-0">
                                {u.is_extra && <span className="text-[10px] font-semibold text-fuchsia-700 bg-fuchsia-100 px-1.5 py-0.5 rounded-full">Extra</span>}
                                {u.is_paypal && <span className="text-[10px] font-semibold text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded-full">PayPal</span>}
                            </span>
                        </button>
                    ))}
                </div>
                {sel && (
                    <p className="text-xs text-slate-500" data-testid="reassign-hint">
                        {sel.is_extra ? 'Este usuario es Extra: la orden pasará a Contabilidad Extra.' : 'Usuario regular: la orden contará en la contabilidad regular.'}
                    </p>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy} data-testid="reassign-cancel">Cancelar</Button>
                    <Button onClick={confirm} disabled={!sel || busy} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="reassign-confirm">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Reasignar a {sel?.name || '…'}</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ReassignUserDialog;
